/**
 * Package name-derivation helpers (11E.4 [D2]).
 *
 * Derived name shape: `{Discipline/Subject} | {Audience} | {N sessions or cycle}`
 * e.g. "Violin | Beginner – Grade 2 | Yearly", "Guitar | All grades | 8 sessions".
 *
 * Audience options are sourced from src/constants/disciplines.ts
 * (getLevelsForDiscipline) per the developer's instruction to reuse the
 * existing grade/discipline reference rather than inventing a new one.
 * Subject.category is free text on the backend, so matching against
 * disciplines.ts is best-effort (substring match) — "All grades" is always
 * offered as a safe fallback.
 */

import { getLevelsForDiscipline } from '@/constants/disciplines';
import type { PackageResponse, PackageType, BillingCycle } from '@features/catalog/api/packages';

export const ALL_GRADES = 'All grades';

const CYCLE_LABELS: Record<BillingCycle, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half-yearly',
  yearly: 'Yearly',
};

/** Audience/grade options for the picker, derived from subject categories. */
export function getAudienceOptions(subjectCategories: string[]): string[] {
  const opts = new Set<string>([ALL_GRADES]);
  subjectCategories.forEach((category) => {
    getLevelsForDiscipline(category).forEach((level) => opts.add(level));
  });
  return Array.from(opts);
}

/** The "N sessions or cycle" segment of the derived name. */
export function deriveCycleLabel(pkg: {
  package_type: PackageType;
  billing_cycle: BillingCycle | null;
  sessions_count: number;
  sessions_unlimited: boolean;
}): string {
  if (pkg.package_type === 'subscription' && pkg.billing_cycle) {
    return CYCLE_LABELS[pkg.billing_cycle];
  }
  if (pkg.sessions_unlimited) return 'Unlimited sessions';
  return `${pkg.sessions_count} session${pkg.sessions_count === 1 ? '' : 's'}`;
}

/** Full derived name preview. Override (name_override) wins when set. */
export function deriveName(
  subjectNames: string[],
  audienceLabel: string,
  cycleLabel: string,
): string {
  const disciplinePart = subjectNames.join(' + ') || 'Package';
  return `${disciplinePart} | ${audienceLabel} | ${cycleLabel}`;
}

/** Resolve the display name for an existing package (override wins). */
export function resolvePackageDisplayName(
  pkg: Pick<
    PackageResponse,
    'name' | 'name_override' | 'package_type' | 'billing_cycle' | 'sessions_count' | 'sessions_unlimited' | 'audience_label'
  >,
  subjectNames: string[],
): string {
  if (pkg.name_override) return pkg.name_override;
  return deriveName(subjectNames, pkg.audience_label, deriveCycleLabel(pkg));
}

